import React from "react";
import { ComponentsHelper } from "../../../../core/services/ComponentHelper";
import { ILookupDictionary } from "../../../../lookup/types/ILookupDictionary";
import { showModal } from "../../../../redux/reducers/MembersPortalReducer";
import { addUsageDetailsRowData, addUsageDetailsShareIPRow } from "../../../../redux/reducers/RepertoireReducer";
import IRepertoireComponentDataItem from "../../../../redux/types/IRepertoireComponentDataItem";
import { IUsagePoolState } from "../../../../redux/types/IUsagePoolState";
import TableHeaderSection from "../../../components/tableHeaderSection/TableHeaderSection";
import { IRepertoireField } from "../../../types/IRepertoireField";
import { IRepertoireStateKeys } from "../../../types/IRepertoireStateKeys";
import { UsageRowView } from "../usageRowView/UsageRowView";
import { USAGE_ROWS_SECTION_KEY } from "../../../Consts";
import { IUsagePoolCore } from "../../../types/usageTypes/IUsagePoolCore";
import CheckboxDataInput from "../../../components/checkboxDataInput/CheckboxDataInput";
import { IComponentView } from "../../../../redux/types/ComponentView";
import { DEFAULT_VIEW_NAME } from "../../../components/modalViews/fieldSettingView/utils/usageResultsFieldUtils";

interface IUsageMaintenanceDetailsGridProps {
    stateKey: IRepertoireStateKeys;
    isExpandable: boolean;
    expandUsageDetailRow?: (index: number) => void;
    tableContents: any;
    pool?: IUsagePoolState;
    dataGridTableData: IRepertoireComponentDataItem;
    changeData?: (value: any, name: IRepertoireStateKeys, objectKey?: string, ipRepresentationsIdsToRemove?: number[]) => void;
    expandedUsageDetailsRow?: number[];
    addUsageDetailsShareIPRow?: typeof addUsageDetailsShareIPRow;
    addUsageDetailsRowData?: typeof addUsageDetailsRowData;
    lookupValues?: ILookupDictionary;
    showModal?: typeof showModal;
    resetMessageBanner?: () => void;
    componentInstance?:string;
    updateUsagesAdvanced?: boolean;
    enableRegroupAndDeleteUI?:boolean;
    expandedUsageMatchingSection?:boolean;
    clearExpandedUsageDetailsRow?: () => void;
    activeComponentView: IComponentView;
    usageNumberInSearchExclude?: string[];
}

const UsageMaintenanceDetailsGrid: React.FC<IUsageMaintenanceDetailsGridProps> = ({
    activeComponentView,
    stateKey,
    isExpandable,
    expandUsageDetailRow,
    tableContents,
    pool,
    dataGridTableData,
    changeData,
    expandedUsageDetailsRow,
    addUsageDetailsShareIPRow,
    addUsageDetailsRowData,
    lookupValues,
    showModal,
    resetMessageBanner,
    updateUsagesAdvanced,
    enableRegroupAndDeleteUI, 
    clearExpandedUsageDetailsRow,
    usageNumberInSearchExclude
}) => {
    const [focusedRow, setFocusedRow] = React.useState<number>(-1);
    const [activeSortHeaderSection, setActiveSortHeaderSection] = React.useState<string>("");
    const [ascending, setAscending] = React.useState<boolean>(true);
    const [usageTableContents, setUsageTableContents] = React.useState(tableContents);
    const [areAllSelected, setAreAllSelected] = React.useState(false);
    const previousTableContentsRef = React.useRef<any>(null);
    
    React.useEffect(() => {
        const newTableContents = tableContents?.filter(item => item.isDeleted !== true) || [];
        const previousTableContents = previousTableContentsRef?.current || [];

        if (JSON.stringify(newTableContents) !== JSON.stringify(previousTableContents)) {
            setUsageTableContents([...newTableContents]);
            previousTableContentsRef.current = newTableContents;
        }
        const allSelected = tableContents.every( item => item.isSelected.value === true);
        setAreAllSelected(allSelected);        
    }, [tableContents])
    
    const sortTableByColumn = (section: string) => {
        const tableContentsCopy = [...usageTableContents];
        if (activeSortHeaderSection !== section) {
            setAscending(false);
        }
        if (tableContentsCopy) {
            tableContentsCopy.sort((a: any, b: any) => {
                const newA = a[section] ? (a[section].value ? a[section].value : a[section]) || "" : "";
                const newB = b[section] ? (b[section].value ? b[section].value : b[section]) || "" : "";


                return ascending
                    ? newA.toString().localeCompare(newB.toString(), undefined, { numeric: true })
                    : newB.toString().localeCompare(newA.toString(), undefined, { numeric: true });
            });
        }

        setActiveSortHeaderSection(section);
        setAscending(!ascending);
        setUsageTableContents(tableContentsCopy);
        clearExpandedUsageDetailsRow();
    };
    
    const onRowClick = (rowNumber) => {
        setFocusedRow(rowNumber);
        if (expandUsageDetailRow) {
            expandUsageDetailRow(rowNumber);
        }
    }
    
    const onExpandAllClicked = () => {
            tableContents.map((row, index) => {
                    expandUsageDetailRow(index);
            });
    }

    const isThisApplyAdjustmentToAllSelectedUsagesButtonDisabled = (indexOfAdjustment: number, usageDetailsIndex: number, usageRowViewIndex: number) => {
            return !usageTableContents?.some(
                (usageRowView, indexOfUsageRowView) =>
                    indexOfUsageRowView !== usageRowViewIndex && 
                    usageRowView?.isSelected?.value
            );
        };
    

        const handleApplyAdjustmentToAllSelectedUsages = (indexOfAdjustment: number, usageRowViewIndex: number) => {
            let adjustmentToCopy = usageTableContents[usageRowViewIndex].usageDetails[indexOfAdjustment]
			const ipiNumber = adjustmentToCopy.ipiNumber;
            usageTableContents.forEach((usageRowView, indexOfUsageRowView) => {
                if (usageRowView?.isSelected && usageRowView?.isSelected?.value && indexOfUsageRowView !== usageRowViewIndex) {
                    usageRowView.usageDetails.forEach((usageDetail, usageRowIndexOfCurrentLoop) => {
                        if (usageDetail.ipiNumber === ipiNumber) { 
                            let adjustmentToSave = [];
                            const totalShare = adjustmentToCopy.adjustments.reduce((acc, each) => each.direction === "From"? acc + parseFloat(each.share): acc, 0);
                            adjustmentToCopy.adjustments.forEach((adjustmentCopy: any, index: number) => {
                                const newAmount = ((parseFloat(adjustmentCopy.share) / totalShare ) * parseFloat(usageDetail.amount))
                                adjustmentToSave.push({...adjustmentCopy, amount: (newAmount.toFixed(10).toString())})
                            })
                            addUsageDetailsShareIPRow(adjustmentToSave, indexOfUsageRowView, usageRowIndexOfCurrentLoop)
                    	}
                    });
                }
            });
        }
    
    const renderTableContents = () => {
        const rows: JSX.Element[] = [];
        if (usageTableContents) {
            let fieldsToRender: any = usageTableContents;
            fieldsToRender && fieldsToRender.map((tableContentItem: any, index: any) => {
                if (updateUsagesAdvanced && pool?.allocationRule !== "PA") {
                    tableContentItem.durationPerPlay.inputType = "text";
                    tableContentItem.amountForAllocationRule.inputType = "text";
					tableContentItem.usageCount.inputType = "text";
				}
				let usageGridData = dataGridTableData;
				
				const fieldMapping = {
					carrierType: "musicSource",
					use: "use",
					dimension1: "dimension1",
					dimension2: "dimension2",
					dimension3: "dimension3",
					dimension4: "dimension4",
					dimension5: "dimension5",
				};

				var dateToCheck = new Date(tableContentItem.usageDate.value);

				const matchingDate = pool?.datesAndDimensions?.find(({ effectiveFrom, effectiveTo }) => {
					var startDate = new Date(effectiveFrom);
					var endDate = effectiveTo ? new Date(effectiveTo) : new Date();
					return dateToCheck >= startDate && dateToCheck <= endDate;
				});

				Object.entries(fieldMapping).forEach(([usageField, datesAndDimensionsField]) => {
					const hasValue = matchingDate?.[datesAndDimensionsField];
					tableContentItem[usageField].inputType = hasValue ? 'text' : 'row';
				});

                rows.push(
                    <UsageRowView
                        activeComponentView={activeComponentView}
                        onRowClick={onRowClick}
                        onExpandAllClicked={onExpandAllClicked}
                        key={index}
                        usage={tableContentItem}
                        index={index}
                        dataGridTableData={usageGridData}
                        lookupValues={lookupValues}
                        changeData={changeData}
                        showModal={showModal}
                        addUsageDetailsShareIPRow={addUsageDetailsShareIPRow}
                        addUsageDetailsRowData={addUsageDetailsRowData}
                        tableContents={usageTableContents}
                        resetMessageBanner={resetMessageBanner}
                        focusedRowNumber={focusedRow}
                        expandedUsageDetailsRow={expandedUsageDetailsRow}
                        enableRegroupAndDeleteUI={enableRegroupAndDeleteUI}
                        handleApplyAdjustmentToAllSelectedUsages={handleApplyAdjustmentToAllSelectedUsages}
                        isThisApplyAdjustmentToAllSelectedUsagesButtonDisabled={isThisApplyAdjustmentToAllSelectedUsagesButtonDisabled}
                        data-testid={`usageRow-${index}`}
                        usageNumberInSearchExclude={usageNumberInSearchExclude}
                    />
                );
            });
        }

        return rows;
    };

    const handleSelectAll = async (value: string | boolean, fieldName: IRepertoireStateKeys) => {
        let usagesCopy = usageTableContents;
    
        usagesCopy.forEach((usage: IUsagePoolCore) => {
            usage[fieldName].value = value;
        });
        setUsageTableContents(usagesCopy);
        setAreAllSelected(!areAllSelected);
    
        for (const usage of usagesCopy) {
            await new Promise((resolve) => {
                setTimeout(() => {
                    changeData(usage, fieldName);
                    resolve(null);
                }, 0);
            });
        }
    };
    



    const getHeaderSections = () => {

        if (dataGridTableData && dataGridTableData.fields) {
            let filteredFields: IRepertoireField[] = [];
            filteredFields = dataGridTableData?.fields?.filter(
                (field: IRepertoireField) => field.componentInstance === USAGE_ROWS_SECTION_KEY && !field.hidden && !field.isInTheBody);

                        if (activeComponentView && activeComponentView.fields && activeComponentView.fields.length > 0 && activeComponentView.viewName !== DEFAULT_VIEW_NAME) {
                filteredFields = activeComponentView?.fields?.filter((field: IRepertoireField) => field.componentInstance === USAGE_ROWS_SECTION_KEY && !field.hidden && !field.isInTheBody);
            }

            let fieldsToRender = filteredFields;

            const headerSections = fieldsToRender.map((section: IRepertoireField, index: number) => (
                <td key={section.name} className={`td`}  id={`usage-maintenance-details-grid-header-${USAGE_ROWS_SECTION_KEY}`}>
                <TableHeaderSection
                    section={section.name === 'isSelected' ? <><CheckboxDataInput
                                                                    label=""
                                                                    fieldName={"isSelected"}
                                                                    value={areAllSelected}
                                                                    changeData={handleSelectAll}
                                                                    readonly={false}
                                                                    isHidden={null}
                                                                    data-testid={`usageRow-checkbox-${index}`}
                                                                /></> : section.data}
                    fieldName={section.name}
                    isSortingActive={section.name === activeSortHeaderSection}
                    ascending={ascending}
                    onClickHeaderSection={sortTableByColumn}
                    sortable={section.name !== 'isSelected'}
                />
            </td>
            ));

            headerSections.sort((r1, r2) => {
                let index1 = dataGridTableData.fields.find(field => field.name === r1.key).order;
                let index2 = dataGridTableData.fields.find(field => field.name === r2.key).order;
    
                return index1 - index2
            });
            return headerSections;
        }
    };

    return (
        <>
            <div key="table" className={'row tableContainer'} id={`usage-maintenance-details-grid-table-container`}>	
                <div className="col-md-12">
                    <table className={["table", stateKey].join(" ")}>
                        <thead key="thead" className={"thead"}>
                            <tr key="headerRow" className="tr">
                                <td></td>
                                {getHeaderSections()}
                            </tr>
                        </thead>
                        <tbody key="body" className="tbody ">
                            {renderTableContents()}
                        </tbody>
                    </table>
                </div>
            </div>
        </>
    );
}

export default UsageMaintenanceDetailsGrid;